import axios from 'axios';

// Function to fetch the secret key from the Flask server
async function fetchSecretKey() {
  try {
    const response = await axios.get('https://josephtauzin.pythonanywhere.com/get-secret', );
    return response.data.secret_key;
  } catch (error) {
    console.error("Error fetching the secret key:", error.response.data);
    return null;
  }
}

function encodeText(text) {
  const encoder = new TextEncoder();
  return encoder.encode(text);
}

// Helper function to create a SHA-256 hash using the Web Crypto API
async function createHash(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

// Helper function to convert Uint8Array to URL-safe Base64 string
function arrayBufferToBase64(buffer) {
  const byteArray = new Uint8Array(buffer);
  const binaryString = byteArray.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
  let base64String = btoa(binaryString);
  
  // Convert to URL-safe Base64
  return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); // Remove padding "="
}

// Helper function to convert URL-safe Base64 string to Uint8Array
function base64ToArrayBuffer(base64) {
  // Convert from URL-safe Base64 to standard Base64
  let base64String = base64.replace(/-/g, '+').replace(/_/g, '/');
  
  // Add padding if needed
  const paddingNeeded = 4 - (base64String.length % 4);
  if (paddingNeeded !== 4) {
    base64String += '='.repeat(paddingNeeded);
  }

  const binaryString = atob(base64String);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// Function to encrypt the userId
async function encryptUserId(userId) {
  const secretKeyString = await fetchSecretKey(); // Fetch and convert the secret key
  if (!secretKeyString) {
    console.error("Secret key could not be retrieved.");
    return null;
  }

  const iv = crypto.getRandomValues(new Uint8Array(12)); // Initialization vector for AES-GCM

  // Convert the secret key string to a CryptoKey object
  const secretKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secretKeyString),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );

  // Encrypt the userId
  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv, // Unique for each encryption
    },
    secretKey, // Secret key in CryptoKey format
    new TextEncoder().encode(userId) // Encode the userId as bytes
  );

  // Convert IV and encrypted data to Base64 strings
  const ivBase64 = arrayBufferToBase64(iv);
  const encryptedBase64 = arrayBufferToBase64(encrypted);

  // Return the Base64-encoded IV and encrypted data as URL-safe strings
  return {
    iv: ivBase64,
    authKey: encryptedBase64,
  };
}

// Function to generate a "random" string based on userId and current time (rounded to 5-minute intervals)
async function generateCode(userId) {
  const secretKey = await fetchSecretKey();
  if (!secretKey) {
    console.error("Secret key could not be retrieved.");
    return null;
  }

  // Round the current time to the nearest 5-minute interval
  const timestamp = Math.floor(Date.now() / (60000 * 5));  // Time in 5-minute intervals

  // Combine userId, timestamp, and secretKey into a string
  const inputString = `${userId}-${timestamp}-${secretKey}`;

  // Create a SHA-256 hash of the input
  const hash = await createHash(inputString);

  // Extract a part of the hash to use as the "random" code (10 characters long)
  const randomNumberString = hash.slice(0, 10);

  return { code: randomNumberString, timestamp };  // Return the code and timestamp used for validation later
}

// Function to validate the code within a 5-minute time window and retrieve the userID if valid
async function validateCode(userId, providedCode, providedTimestamp) {
  const secretKey = await fetchSecretKey();
  if (!secretKey) {
    console.error("Secret key could not be retrieved.");
    return { valid: false, userId: null };
  }

  // Get the current time (rounded to 5-minute intervals)
  const currentTimestamp = Math.floor(Date.now() / (60000 * 5));  // Time in 5-minute intervals

  // Check codes for the current 5-minute interval and the previous one (to account for minor time differences)
  for (let offset = 0; offset <= 1; offset++) {
    const timestampToCheck = currentTimestamp - offset;
    const inputString = `${userId}-${timestampToCheck}-${secretKey}`;

    // Generate the code for this timestamp
    const hash = await createHash(inputString);
    const generatedCode = hash.slice(0, 10);

    // Check if the provided code matches the generated one and if the timestamp matches
    if (generatedCode === providedCode ) {
      return { valid: true, userId };  // Return valid status and the userID
    }
  }

  return { valid: false, userId: null };  // Invalid code
}

// Function to decrypt the userId
async function decryptUserId(encryptedData) {
  const { iv: ivBase64, authKey: encryptedBase64 } = encryptedData;
  const secretKeyString = await fetchSecretKey();
  if (!secretKeyString) {
    return null;
  }

  // Decode Base64 IV and encrypted data back to Uint8Array
  const iv = base64ToArrayBuffer(ivBase64);
  const encrypted = base64ToArrayBuffer(encryptedBase64);

  // Convert the secret key string to a CryptoKey object
  const secretKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secretKeyString),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );

  // Decrypt the data
  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv, // The IV must be the same as the one used for encryption
    },
    secretKey,
    encrypted
  );

  // Decode the decrypted data into a string (userId)
  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

// Function to create an authentication URL
async function createAuthUrl(userId) {
  const data = await encryptUserId(userId);
  const authKey = data.authKey;
  const iv = data.iv;
  if (!authKey) {
    console.error("Failed to generate authKey.");
    return null;
  }

  const { code, timestamp } = await generateCode(userId);
  if (!code || !timestamp) {
    console.error("Failed to generate code and timestamp.");
    return null;
  }
  var Company = "GameTheory";
  const url = `https://tutorspace.app/auth?authKey=${encodeURIComponent(authKey)}&iv=${encodeURIComponent(iv)}&code=${code}&timestamp=${timestamp}&companyCode=${Company}`;
  return url;
}

export { createAuthUrl, validateCode, decryptUserId, fetchSecretKey, generateCode };
